import type { DragEndEvent } from '@dnd-kit/core';
import type { ISection } from 'src/types/menu.types';

export type IDropResult =
  | {
      kind: 'reorder';
      sectionId: string;
      itemId: string;
      fromIndex: number;
      toIndex: number;
      newDisplayOrder: number;
    }
  | {
      kind: 'move';
      itemId: string;
      fromSectionId: string;
      toSectionId: string;
      newDisplayOrder: number;
    };

export const appendedDisplayOrder = (section: ISection): number =>
  section.items.length ? Math.max(...section.items.map(({ displayOrder }) => displayOrder)) + 1 : 0;

// Both sortable cards and section drop zones carry data.sectionId, so one lookup resolves the
// target whether the pointer is over a card or over a section's empty space.
export const getDropResult = (
  event: DragEndEvent,
  sections: ISection[],
  originSectionId?: string,
): IDropResult | null => {
  const { active, over } = event;
  if (!over) return null;

  const fromSectionId = originSectionId ?? (active.data.current?.sectionId as string | undefined);
  const toSectionId = over.data.current?.sectionId as string | undefined;
  if (!fromSectionId || !toSectionId) return null;

  const source = sections.find((section) => section.sectionId === fromSectionId);
  if (!source) return null;

  const fromIndex = source.items.findIndex(({ itemId }) => itemId === active.id);
  if (fromIndex === -1) return null;

  if (fromSectionId === toSectionId) {
    const toIndex = source.items.findIndex(({ itemId }) => itemId === over.id);
    if (toIndex === -1 || fromIndex === toIndex) return null;

    return {
      kind: 'reorder',
      sectionId: fromSectionId,
      itemId: String(active.id),
      fromIndex,
      toIndex,
      newDisplayOrder: source.items[toIndex].displayOrder,
    };
  }

  const destination = sections.find((section) => section.sectionId === toSectionId);
  if (!destination) return null;

  return {
    kind: 'move',
    itemId: String(active.id),
    fromSectionId,
    toSectionId,
    newDisplayOrder: appendedDisplayOrder(destination),
  };
};
